import { useState } from 'react';
import { ArrowLeftRight, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { isCapable } from '../structureLogic';
import type { TransferOut } from '../types';
import { useFieldTransfers } from '../hooks/useStructureQueries';
import { TransferFieldDialog, type FarmOption } from './TransferFieldDialog';

interface FieldTransfersTabProps {
  fieldId: string;
  fieldName: string;
  currentFarmId: string;
  currentFarmName?: string | null;
  areaHa?: number | null;
  /** Все хозяйства тенанта — для селекта приёмника в модалке передачи. */
  farms: FarmOption[];
  /** Может ли пользователь передавать (админ). */
  canManage: boolean;
}

function fmtDate(iso: string): string {
  // ISO date (YYYY-MM-DD) → ДД.ММ.ГГГГ.
  const d = iso.slice(0, 10).split('-');
  if (d.length === 3) return `${d[2]}.${d[1]}.${d[0]}`;
  return iso;
}

/**
 * Вкладка «Передачи» на карточке поля (AK-346): кнопка «Передать в другое
 * хозяйство» + таймлайн истории передач из GET /fields/{id}/transfers.
 * Первая запись (самая свежая) — «сейчас здесь».
 */
export function FieldTransfersTab({
  fieldId,
  fieldName,
  currentFarmId,
  currentFarmName,
  areaHa,
  farms,
  canManage,
}: FieldTransfersTabProps) {
  const transfersQ = useFieldTransfers(fieldId, isCapable('transferHistory'));
  const [dialogOpen, setDialogOpen] = useState(false);

  const transfers: TransferOut[] = transfersQ.data ?? [];

  return (
    <div className="space-y-4">
      {canManage && isCapable('transferField') && (
        <div className="flex justify-end">
          <Button size="sm" onClick={() => setDialogOpen(true)}>
            <ArrowLeftRight className="h-4 w-4" />
            Передать в другое хозяйство
          </Button>
        </div>
      )}

      <Card>
        <CardContent className="p-4">
          {transfersQ.isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 2 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : transfers.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-8 text-center text-muted-foreground">
              <History className="h-6 w-6" />
              <p className="text-sm">
                Поле пока не передавалось. Здесь появится история — куда, когда и от
                кого.
              </p>
            </div>
          ) : (
            <ol className="ml-2 border-l-2 border-border pl-5">
              {transfers.map((t, idx) => (
                <li key={t.id} className="relative pb-4 last:pb-0">
                  <span
                    className={
                      idx === 0
                        ? 'absolute -left-[26px] top-1 h-2.5 w-2.5 rounded-full border-2 border-accent-brand bg-background'
                        : 'absolute -left-[26px] top-1 h-2.5 w-2.5 rounded-full border-2 border-muted-foreground bg-secondary'
                    }
                    aria-hidden
                  />
                  <div className="text-xs tabular-nums text-muted-foreground">
                    {fmtDate(t.transferred_on)}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
                    Передано в «{t.to_farm_name ?? '—'}»
                    {idx === 0 && (
                      <Badge variant="secondary" className="px-1.5 py-0 text-[10px] text-primary">
                        сейчас здесь
                      </Badge>
                    )}
                  </div>
                  {(t.from_farm_name || t.note) && (
                    <div className="text-xs text-muted-foreground">
                      {t.from_farm_name && <>Из «{t.from_farm_name}»</>}
                      {t.note && (
                        <>
                          {t.from_farm_name ? ' · ' : ''}«{t.note}»
                        </>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ol>
          )}
        </CardContent>
      </Card>

      <TransferFieldDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        fieldId={fieldId}
        fieldName={fieldName}
        currentFarmId={currentFarmId}
        currentFarmName={currentFarmName}
        areaHa={areaHa}
        farms={farms}
        onTransferred={() => transfersQ.refetch()}
      />
    </div>
  );
}
